import { makeAutoObservable, reaction, runInAction } from "mobx";
import { fromPairs, IdMap } from "../collections";
import { ActivationState, PromotionInstance } from "./PromotionInstance";
import { PromotionSolver, } from "./PromotionSolver";
export class PromotionScheme {
    constructor(params) {
        this.productInstancePriceService = params.productInstancePriceService;
        this.promotionSolverVariableAssignmentFactory =
            params.promotionSolverVariableAssignmentFactory;
        this._promotionInstances = params.promotionInstances;
        this.recomputeExclusions();
        makeAutoObservable(this);
        this.startSolver();
    }
    startSolver() {
        // NOTICE We want solver to be active during the whole session, that's why this reaction is not disposed anywhere
        reaction(() => this.promotionSolverVariableAssignmentFactory.assignment, (variableAssignment) => {
            console.log("Solver invoked because cart has changed");
            const solver = new PromotionSolver({
                productInstancePriceService: this.productInstancePriceService,
                variableAssignment,
            });
            this.setPromotionInstances(solver.promotionInstances);
        }, {
            equals: (a, b) => a.eq(b),
            fireImmediately: true,
        });
    }
    setPromotionInstances(newInstances) {
        runInAction(() => {
            this._promotionInstances = this.reconcile(this._promotionInstances, newInstances);
            this.recomputeExclusions();
            this.enforceExclusions();
        });
    }
    enforceExclusions() {
        this._promotionInstances.objects.forEach((instance) => {
            if (instance.activationState === ActivationState.UserEnabled) {
                // Re-trigger setActive to cascade deactivateByExclusion on excluded instances
                instance.setActive(true);
            }
        });
    }
    /**
     * Preserves user activation decisions and freebie choices across solver re-runs.
     *
     * Instances are matched by id (deterministic: promotionTypeId + index within type),
     * so the same logical promotion slot gets the same id across solver runs.
     *
     * Freebie choices are always preserved (even when the instance is currently
     * excluded), since the user's freebie selection should survive exclusion cycles.
     */
    reconcile(oldInstances, newInstances) {
        for (const newInst of newInstances.objects) {
            const oldInst = oldInstances.find(newInst.id);
            if (oldInst === null)
                continue;
            const solverState = newInst.activationState;
            // Transfer freebie (setFreebie has side-effect of setting UserEnabled)
            if (oldInst.freebie !== null) {
                newInst.setFreebie(oldInst.freebie);
            }
            // Restore correct activation state: user decision from old, or solver state
            newInst.setActivationState(ActivationState.isUserDecision(oldInst.activationState)
                ? oldInst.activationState
                : solverState);
        }
        return newInstances;
    }
    get promotionInstances() {
        return this._promotionInstances;
    }
    clear() {
        this._promotionInstances = IdMap.empty();
    }
    recomputeExclusions() {
        this._promotionInstances.objects.forEach((promotionInstanceA) => {
            const excludedInstances = this._promotionInstances.filter((promotionInstanceB) => PromotionInstance.excludes(promotionInstanceA, promotionInstanceB));
            promotionInstanceA.setExcludedInstances(excludedInstances);
        });
    }
    disableMarketingPromotionInstances() {
        this._promotionInstances.objects.forEach((promotionInstance) => {
            if (promotionInstance.promotionType.requiresMarketing) {
                promotionInstance.setActive(false);
            }
        });
    }
    get storageData() {
        return fromPairs(this._promotionInstances.objects.map((promotionInstance) => [
            promotionInstance.id.value,
            promotionInstance.storageData,
        ]));
    }
}
